/**
 * Runnable Playwright/TypeScript project egress (anti-lock-in).
 * Export-only — never mutates flows or claims Rynora execution equivalence.
 */
import { z } from 'zod';

export const PLAYWRIGHT_EXPORT_STATUSES = ['fully_exportable', 'partially_exportable', 'unsupported'] as const;
export type PlaywrightExportStatus = (typeof PLAYWRIGHT_EXPORT_STATUSES)[number];

export const PlaywrightExportGovernanceSchema = z.object({
  exportOnly: z.literal(true),
  autoApply: z.literal(false),
  autoMutate: z.literal(false),
  autoPublish: z.literal(false),
  autoBlocksRelease: z.literal(false),
  claimsSafeSkip: z.literal(false),
  claimsExecutionEquivalence: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
});
export type PlaywrightExportGovernance = z.infer<typeof PlaywrightExportGovernanceSchema>;

export const PLAYWRIGHT_EXPORT_GOVERNANCE: PlaywrightExportGovernance = {
  exportOnly: true,
  autoApply: false,
  autoMutate: false,
  autoPublish: false,
  autoBlocksRelease: false,
  claimsSafeSkip: false,
  claimsExecutionEquivalence: false,
  recommendationsAreNotTruth: true,
};

export const PlaywrightExportStepResultSchema = z.object({
  stepId: z.string().max(120),
  stepIndex: z.number().int().nonnegative(),
  stepType: z.string().max(80),
  title: z.string().max(300),
  status: z.enum(PLAYWRIGHT_EXPORT_STATUSES),
  reason: z.string().max(500),
  generatedLines: z.number().int().nonnegative().default(0),
});
export type PlaywrightExportStepResult = z.infer<typeof PlaywrightExportStepResultSchema>;

export const PlaywrightExportPreviewSchema = z.object({
  attribution: z.literal('playwright_project_export'),
  projectId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string().max(300),
  publishedVersion: z.number().int().nonnegative(),
  status: z.enum(PLAYWRIGHT_EXPORT_STATUSES),
  summary: z.string().max(600),
  why: z.array(z.string().max(300)).max(12).default([]),
  governance: PlaywrightExportGovernanceSchema,
  counts: z.object({
    totalSteps: z.number().int().nonnegative(),
    fullyExportable: z.number().int().nonnegative(),
    partiallyExportable: z.number().int().nonnegative(),
    unsupported: z.number().int().nonnegative(),
    secretsRedacted: z.number().int().nonnegative(),
  }),
  steps: z.array(PlaywrightExportStepResultSchema).max(200),
  files: z.array(z.object({ path: z.string().max(200), description: z.string().max(300) })).max(20),
  previewSpecExcerpt: z.string().max(8000).optional(),
  downloadPath: z.string().max(500),
});
export type PlaywrightExportPreview = z.infer<typeof PlaywrightExportPreviewSchema>;

export const PlaywrightExportQuerySchema = z.object({
  format: z.enum(['zip']).default('zip'),
});
export type PlaywrightExportQuery = z.infer<typeof PlaywrightExportQuerySchema>;
